import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { SpeakingModelAnswer, type SpeakingModelAnswerInfo } from 'modules/answerAnalysis';
import { LockedInsight } from 'modules/examInsights';

/** One stored Speaking answer, with a short-lived signed link. */
export interface SpeakingClip {
  key: string;
  part: number;
  prompt: string;
  durationSec: number | null;
  url: string | null;
  /** The AI's transcript, once it has made one. */
  transcript?: string | null;
  /** Candidate's own review only: a prompt they did not answer (no recording), listed so a model answer can be asked for it. */
  answered?: false;
  /** Candidate's own review only: a model answer can be asked for, and the one already written. */
  modelAnswerAvailable?: boolean;
  /** Locked IELTS review: the transcript was left out; it comes with the plan. */
  transcriptLocked?: boolean;
  /** Locked IELTS review: the model answer comes with the plan. */
  modelAnswerLocked?: boolean;
  modelAnswer?: SpeakingModelAnswerInfo | null;
}

interface Props {
  recordings: SpeakingClip[];
  /**
   * The candidate's own review passes these so each recording can offer a model answer under its transcript;
   * a grader's view leaves them out and gets none.
   */
  modelAnswerFor?: { attemptId: string; questionId: string };
  /**
   * `grader` puts the Part 2 long turn first (it carries most of the evidence) and keeps transcripts folded.
   * `review` is the candidate's own read-back: test order, transcripts open.
   */
  variant?: 'grader' | 'review';
}

const clock = (sec: number | null) =>
  sec == null ? '' : `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}`;

/** Speaking answers: each prompt with its recording and, once made, the AI's transcript of it. */
export function SpeakingClips({ recordings, variant = 'grader', modelAnswerFor }: Props) {
  const { t } = useTranslation();
  const [rate, setRate] = useState(1);
  const ordered =
    variant === 'grader'
      ? [...recordings].sort((a, b) => (a.part === 2 ? -1 : 0) - (b.part === 2 ? -1 : 0))
      : recordings;
  const total = recordings.reduce((sum, r) => sum + (r.durationSec ?? 0), 0);
  const setSpeed = (el: HTMLAudioElement | null) => {
    if (el) el.playbackRate = rate;
  };
  if (!recordings.length) {
    return <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">{t('exam.speaking.no_recordings')}</p>;
  }
  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {t('exam.speaking.recordings_summary', { count: recordings.length, time: clock(total) })}
        </p>
        <button
          type="button"
          onClick={() => setRate((r) => (r === 1 ? 1.25 : 1))}
          aria-pressed={rate !== 1}
          title={t('exam.speaking.speed_help')}
          className={cn(
            'h-8 rounded-md border px-2.5 font-mono text-xs font-semibold transition-colors',
            rate !== 1 ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground hover:bg-muted',
          )}
        >
          {rate}×
        </button>
      </div>
      <ol className="space-y-2">
        {ordered.map((clip) => (
          <li
            key={clip.key}
            className={cn(
              'min-w-0 rounded-lg border bg-card p-3',
              variant === 'grader' && clip.part === 2 ? 'border-2 border-foreground/80' : 'border-border',
            )}
          >
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <p className="min-w-0 break-words text-sm text-foreground">
                <span className="font-semibold">{t('exam.speaking.part_n', { n: clip.part })}</span> · {clip.prompt.split('\n')[0]}
              </p>
              <span className="shrink-0 font-mono text-xs text-muted-foreground">{clock(clip.durationSec)}</span>
            </div>
            {clip.answered === false ? (
              <p className="text-xs text-muted-foreground">{t('exam.speaking.not_answered')}</p>
            ) : clip.url ? (
              <audio ref={setSpeed} controls preload="none" src={clip.url} className="h-9 w-full max-w-full" onPlay={(e) => (e.currentTarget.playbackRate = rate)} />
            ) : (
              <p className="text-xs text-destructive">{t('exam.speaking.audio_unavailable')}</p>
            )}
            {clip.transcript && (
              <details className="mt-2" open={variant === 'review'}>
                <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground">
                  {t('exam.speaking.transcript')}
                </summary>
                <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">{clip.transcript}</p>
              </details>
            )}
            {clip.transcriptLocked && (
              <LockedInsight title={t('exam_insights.transcript_title')} description={t('exam_insights.transcript_body')} lines={3} />
            )}
            {modelAnswerFor && clip.modelAnswerLocked && (
              <LockedInsight title={t('exam_insights.model_answer_title')} description={t('exam_insights.model_answer_body')} lines={4} />
            )}
            {modelAnswerFor && clip.modelAnswerAvailable && (
              <SpeakingModelAnswer
                attemptId={modelAnswerFor.attemptId}
                questionId={modelAnswerFor.questionId}
                clipKey={clip.key}
                saved={clip.modelAnswer ?? null}
              />
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

export default SpeakingClips;
